/** Isolated home-walk-adapter/1 contract. This is not a backend catalog registration. */
import type { CharacterState, PetPrivateSummary } from "@/shared/contracts";
import { manifestDigest } from "@/features/pet_motion/digest";
import { headerProblems, mergeMotionSet, prepareMotion, type MotionClip, type MotionFrame, type MotionSet, type PreparedMotion } from "@/features/pet_motion/manifest";

export const HOME_WALK_CATALOG = "home-walk-adapter/1";
export type HomeFacing = "front" | "back" | "left" | "right";
export type HomePresence = "home" | "away" | "gone" | "unknown";
export type HomeMotionMode = "free" | "moving" | "sleeping" | "busy" | "eating" | "unknown";
export type HomeMotionProof = "none" | "metadata" | "loaded_metadata" | "fixture_control";
export const HOME_FACINGS: readonly HomeFacing[] = ["front", "back", "left", "right"];
const OLD: Record<string, readonly [string, string, boolean]> = {
  stand_idle: ["STAND", "STAND", true], stand_to_sit: ["STAND", "SIT", false],
  sit_to_stand: ["SIT", "STAND", false], sit_idle: ["SIT", "SIT", true],
  sit_to_lie: ["SIT", "LIE", false], lie_to_sit: ["LIE", "SIT", false],
  lie_idle: ["LIE", "LIE", true], lie_to_stand: ["LIE", "STAND", false],
};
/**
 * clip-catalog-1（后端 web_character/motion.py 的居家目录；读口头部写死这个版本号，路由头部不改）除了旧 8 段，还认 9 个走路段名：
 * stand_to_walk_、walk_loop_、walk_to_stand_ × left、right、front（6796 MOTION-WALK-CATALOG-01 只增不删补进目录，起止、循环、朝向和 expected() 一样；
 * I 10-09 定由前端放宽，并进 HOME-FIGURE-APPEAR-01）。背对镜头那一组以后在目录里叫 up（I 10-09 定 back = up，第二批出图时登记），这次不认；
 * clip-catalog-1 里出现 back、up 或别的名字照旧整套拒（unrecognized_clip）。home-walk-adapter/1 照旧按 expected() 认四个朝向。
 */
const CATALOG1_WALK: ReadonlySet<string> = new Set((["left", "right", "front"] as const).flatMap((f) => [`stand_to_walk_${f}`, `walk_loop_${f}`, `walk_to_stand_${f}`]));
export interface HomeMotionInput {
  pet: PetPrivateSummary;
  state: CharacterState | null;
  /** null/undefined means unproved, never a fabricated body version. */
  bodyRevision: number | null | undefined;
  identityGeneration: string;
  motion: MotionSet | null;
  presence: HomePresence;
  mode: HomeMotionMode;
  desiredFacing: HomeFacing | null;
  reducedMotion?: boolean;
  paused?: boolean;
  /** Explicit component harness opt-in; it can never grant formal path motion. */
  synthetic?: boolean;
}
export interface HomeMotionPolicy {
  motion: MotionSet | null;
  prepared: PreparedMotion | null;
  directions: readonly HomeFacing[];
  reason: string;
  proof: HomeMotionProof;
  identityKey: string;
  bodyVisible: boolean;
  canPlayWalk: boolean;
}

function expected(key: string): readonly [string, string, boolean, string] | null {
  const old = OLD[key];
  if (old) return [...old, "front"];
  for (const direction of HOME_FACINGS) {
    if (key === `stand_to_walk_${direction}`) return ["STAND", "WALK", false, direction];
    if (key === `walk_loop_${direction}`) return ["WALK", "WALK", true, direction];
    if (key === `walk_to_stand_${direction}`) return ["WALK", "STAND", false, direction];
  }
  return null;
}
const area = (f: MotionFrame) => (f.content_box.right - f.content_box.left) * (f.content_box.bottom - f.content_box.top);
function continuous(a: MotionFrame, b: MotionFrame, set: MotionSet, seam: boolean): boolean {
  const ratio = area(b) / area(a);
  const tolerance = 2 * Math.max(set.canvas.width, set.canvas.height) / 768;
  return ratio >= (seam ? 0.95 : 0.8) && ratio <= (seam ? 1.05 : 1.25) &&
    Math.hypot((a.anchor.x - b.anchor.x) * set.canvas.width, (a.anchor.y - b.anchor.y) * set.canvas.height) <= tolerance;
}
function validHomeClip(set: MotionSet, clip: MotionClip): boolean {
  const spec = expected(clip.clip_key);
  if (!spec || clip.from_state !== spec[0] || clip.to_state !== spec[1] || clip.loop !== spec[2] || clip.facing !== spec[3] || clip.fallback_state !== clip.to_state) return false;
  if (clip.anchor != null || !clip.frames?.length) return false;
  try { if (manifestDigest(set, clip) !== clip.manifest_sha256) return false; } catch { return false; }
  const tolerance = 2 * Math.max(set.canvas.width, set.canvas.height) / 768;
  for (const frame of clip.frames) {
    const box = frame.content_box;
    if (!box || frame.grounded === false || !frame.anchor?.measured || ![box.left, box.right, box.top, box.bottom, frame.anchor.x, frame.anchor.y].every(Number.isFinite)) return false;
    if (Math.hypot(frame.anchor.x * set.canvas.width - (box.left + box.right) / 2, frame.anchor.y * set.canvas.height - box.bottom) > tolerance) return false;
    if (Math.hypot((frame.anchor.x - set.anchor.x) * set.canvas.width, (frame.anchor.y - set.anchor.y) * set.canvas.height) > tolerance) return false;
  }
  for (let i = 1; i < clip.frames.length; i += 1) if (!continuous(clip.frames[i - 1], clip.frames[i], set, false)) return false;
  if (clip.loop && !continuous(clip.frames.at(-1)!, clip.frames[0], set, false)) return false;
  return true;
}

/** Only retain an explicitly returned bad segment within the same valid header/identity round. */
export function homeMotionPolicy(input: HomeMotionInput, previous: MotionSet | null = null): HomeMotionPolicy {
  const { pet, state, bodyRevision, identityGeneration, motion, presence, mode, desiredFacing } = input;
  const active = state?.active;
  const neutral = active?.assets.find((a) => a.pose === "neutral_full");
  const identityKey = JSON.stringify([identityGeneration, pet.pet_id, active?.character_set_id, active?.revision, active?.reference_version, neutral?.content_sha256, active?.style_version, bodyRevision]);
  const bodyVisible = presence === "home";
  const reject = (reason: string): HomeMotionPolicy => ({ motion: null, prepared: null, directions: [], reason, proof: "none", identityKey, bodyVisible, canPlayWalk: false });
  if (!identityGeneration || !active || state?.pet_id !== pet.pet_id || active.pet_id !== pet.pet_id) return reject("identity_unproved");
  if (!motion) return reject("motion_unavailable_or_revoked");
  if (headerProblems(motion).length || motion.purpose !== "home" || motion.anchor_rule !== "hind_paws_midpoint" || !Number.isSafeInteger(motion.canvas.width) || !Number.isSafeInteger(motion.canvas.height) || !["keyframe_inbetween", "video_extract", "fixture"].includes(motion.generation_method)) return reject("invalid_header");
  if (motion.clip_catalog_version !== "clip-catalog-1" && motion.clip_catalog_version !== HOME_WALK_CATALOG) return reject("unrecognized_catalog");
  if (motion.clips.some((c) => !expected(c.clip_key) || (motion.clip_catalog_version === "clip-catalog-1" && !OLD[c.clip_key] && !CATALOG1_WALK.has(c.clip_key)))) return reject("unrecognized_clip");
  const sanitized = { ...motion, clips: motion.clips.map((c) => {
    const older = previous?.clips.find((p) => p.clip_key === c.clip_key && p.revision > c.revision);
    // mergeMotionSet itself enforces the full same-header condition before retaining.
    return !older && validHomeClip(motion, c) ? c : { ...c, frames: [] };
  }) };
  const merged = mergeMotionSet(previous, sanitized);
  const prepared = prepareMotion(merged, { set: active, ...(bodyRevision !== undefined ? { bodyRevision } : {}) });
  if (!prepared) return reject("identity_or_segments_invalid");
  const good = prepared.clips.filter((c) => validHomeClip(prepared.set, c));
  const stand = good.find((c) => c.clip_key === "stand_idle");
  if (!stand) return reject("stand_stance_missing");
  const directions = HOME_FACINGS.filter((f) => {
    const start = good.find((c) => c.clip_key === `stand_to_walk_${f}`);
    const loop = good.find((c) => c.clip_key === `walk_loop_${f}`);
    const stop = good.find((c) => c.clip_key === `walk_to_stand_${f}`);
    if (!start || !loop || !stop || motion.mirror_allowed !== false || loop.frames.length < 2 || new Set(loop.frames.map((x) => x.content_sha256)).size < 2 || loop.interrupt_frames.length < 2) return false;
    const seam = (a: MotionFrame, b: MotionFrame) => a.content_sha256 === b.content_sha256 && continuous(a, b, motion, true);
    return seam(stand.frames[0], start.frames[0]) && seam(start.frames.at(-1)!, loop.frames[0]) && seam(loop.frames[0], stop.frames[0]) && seam(stop.frames.at(-1)!, stand.frames[0]);
  });
  const qualified = prepareMotion({ ...prepared.set, clips: good }, { set: active, ...(bodyRevision !== undefined ? { bodyRevision } : {}) })!;
  let reason = "metadata_ready";
  if (!bodyVisible) reason = presence;
  else if (mode === "sleeping" || mode === "busy" || mode === "eating" || mode === "unknown") reason = mode;
  else if (input.reducedMotion) reason = "reduced_motion";
  else if (input.paused) reason = "paused";
  else if (bodyRevision == null || !Number.isSafeInteger(bodyRevision) || bodyRevision < 0) reason = "body_revision_unknown";
  else if (motion.generation_method === "fixture" && !input.synthetic) reason = "fixture_not_formal";
  else if (!desiredFacing || !directions.includes(desiredFacing)) reason = "direction_triplet_missing_or_invalid";
  return { motion: qualified.set, prepared: qualified, directions, reason, proof: input.synthetic || motion.generation_method === "fixture" ? "fixture_control" : "metadata", identityKey, bodyVisible, canPlayWalk: reason === "metadata_ready" };
}

/** Filter before route/loop selection: the generic player must never choose another direction. */
export function motionForFacing(policy: HomeMotionPolicy, facing: HomeFacing | null, includeWalk: boolean): MotionSet | null {
  if (!policy.motion) return null;
  return { ...policy.motion, clips: policy.motion.clips.filter((c) => !!OLD[c.clip_key] || (includeWalk && c.facing === facing && (c.from_state === "WALK" || c.to_state === "WALK"))) };
}
